import { makeStyles } from '@mui/styles'
import { IconButton, Tooltip, Typography } from '@mui/material'
import { loadStateFromLocalStorage } from '../redux/utilsLocalStorage'
import InfoIcon from '@mui/icons-material/Info'
import { BlockMath, InlineMath } from 'react-katex'
import 'katex/dist/katex.min.css'

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'baseline',
    flex: 1,
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  label: {
    fontSize: 14,
    color: theme.palette.primary.main,
    textTransform: 'uppercase',
  },
  value: {
    fontSize: 28,
    fontWeight: '400',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    textTransform: 'none',
  },
  valueSmall: {
    fontSize: 18,
    fontWeight: '400',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    textTransform: 'none',
    color: theme.palette.text.secondary,
  },
  tooltip: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontSize: theme.typography.fontSize,
  },
  arrow: {
    color: theme.palette.primary.main,
  },
  info: { color: theme.palette.primary.main },
  formatTooltip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },

  tooltipOffset: {
    transform: 'translateY(-15px)',
  },
}))

const getNormalizedUnit = unit => {
  const unitMapping = {
    kW: 'kW',
    kWh: 'kW',
    MW: 'MW',
    MWh: 'MW',
  }
  return unitMapping[unit] || 'kW'
}

const METRICS = {
  nmae: {
    unit: '%',
    format: v => v.toFixed(2),
    formula: `
      \\text{NMAE}_{\\%} = \\frac{100}{N \\cdot P_{inst}}
      \\sum_{i=1}^{N} |P_i - F_i|
    `,
    legend: {
      P_i: 'observed power',
      F_i: 'forecasted power',
      'P_{inst}': 'installed capacity',
      N: 'number of time steps',
    },
  },
  normalized: {
    unit: '%',
    format: v => v.toFixed(2),
    formula: `
      \\text{NMAE}_{\\%} =
      \\frac{
        \\left( \\sum_{i=1}^{N} \\left| P_i - F_i \\right| \\right) \\cdot 100
      }{
        \\sum_{i=1}^{N} P_i^{\\text{avail}}
      }
    `,
    legend: {
      P_i: 'observed power',
      F_i: 'forecasted power',
      'P_i^{\\text{avail}}': 'available capacity at time $i$',
      N: 'number of valid time steps',
    },
  },

  nrmse: {
    unit: '%',
    format: v => v.toFixed(2),
    formula: `
      \\text{NRMSE}_{\\%} = \\frac{100}{P_{inst}}
      \\sqrt{\\frac{1}{N} \\sum_{i=1}^{N} (P_i - F_i)^2}
    `,
    legend: {
      P_i: 'observed power',
      F_i: 'forecasted power',
      'P_{inst}': 'installed capacity',
      N: 'number of time steps',
    },
  },
  nbias: {
    unit: '%',
    format: v => v.toFixed(2),
    formula: `
      \\text{NBIAS}_{\\%} = \\frac{100}{N \\cdot P_{inst}}
      \\sum_{i=1}^{N} (F_i - P_i )
    `,
    legend: {
      P_i: 'observed power',
      F_i: 'forecasted power',
      'P_{inst}': 'installed capacity',
      N: 'number of time steps',
      Note: 'A positive value indicates systematic over-forecasting; a negative value indicates under-forecasting',
    },
  },
  mae: {
    unit: selectedUnit => selectedUnit,
    format: v => v.toFixed(2),
    formula: `
      \\text{MAE} = \\frac{1}{N} \\sum_{i=1}^{N} |P_i - F_i|
    `,
    legend: {
      P_i: 'observed (measured) power at time step $i$',
      F_i: 'forecasted power at time step $i$',
      N: 'number of valid time steps included in the evaluation',
    },
  },
  rmse: {
    unit: selectedUnit => selectedUnit,
    format: v => v.toFixed(2),
    formula: `
      \\text{RMSE} = \\sqrt{\\frac{1}{N} \\sum_{i=1}^{N} (P_i - F_i)^2}
    `,
    legend: {
      P_i: 'observed (measured) power at time step $i$',
      F_i: 'forecasted power at time step $i$',
      N: 'number of valid time steps included in the evaluation',
    },
  },
  bias: {
    unit: selectedUnit => selectedUnit,
    format: v => v.toFixed(2),
    formula: `
      \\text{BIAS} = \\frac{1}{N} \\sum_{i=1}^{N} (F_i - P_i)
    `,
    legend: {
      P_i: 'observed (measured) power at time step $i$',
      F_i: 'forecasted power at time step $i$',
      N: 'number of valid time steps included in the evaluation',
    },
  },
}

const renderMathText = text =>
  text
    .split(/(\$.*?\$)/g)
    .map((part, idx) =>
      part.startsWith('$') && part.endsWith('$') ? (
        <InlineMath key={idx} math={part.slice(1, -1)} />
      ) : (
        <span key={idx}>{part}</span>
      ),
    )

const MetricTooltip = ({ metric, classes, note }) => {
  if (!metric?.formula) return null

  return (
    <Tooltip
      arrow
      placement="top"
      classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
      title={
        <div style={{ textAlign: 'left' }}>
          <BlockMath math={metric.formula} />

          {metric.legend && (
            <div style={{ marginTop: 8, fontSize: '0.85em' }}>
              {Object.entries(metric.legend).map(([symbol, description]) => (
                <div key={symbol}>
                  <InlineMath math={symbol} /> &mdash;{' '}
                  {renderMathText(description)}
                </div>
              ))}
            </div>
          )}

          {note && (
            <div
              style={{ marginTop: 8, fontSize: '0.85em', fontStyle: 'italic' }}
            >
              {note}
            </div>
          )}
        </div>
      }
    >
      <span>
        <IconButton size="small" disableTouchRipple aria-label="info">
          <InfoIcon fontSize="small" className={classes.info} />
        </IconButton>
      </span>
    </Tooltip>
  )
}

const POWER_METRICS = new Set(['mae', 'rmse', 'bias'])

const Parameters = ({
  label,
  value,
  normalizedValue,
  whitespace = false,
  small = false,
}) => {
  const classes = useStyles()

  const storedUnit = loadStateFromLocalStorage('unit') || 'kW'
  const selectedUnit = getNormalizedUnit(storedUnit)
  const hasNormalizedValue = normalizedValue != null

  const isNMAE = label === 'nmae'
  const showNormalized = isNMAE && hasNormalizedValue

  const formatValue = (metricKey, val) => {
    const m = METRICS[metricKey]
    if (!m || val == null || typeof val !== 'number' || isNaN(val)) return '-'

    const unit = typeof m.unit === 'function' ? m.unit(selectedUnit) : m.unit

    let value = val

    if (selectedUnit === 'MW' && POWER_METRICS.has(metricKey)) {
      value = value / 1000
      value = Number(value.toFixed(2))
    }

    return `${m.format(value)}${unit}`
  }

  const formatLabel = value => {
    if (!value) return value // handle empty/null

    if (whitespace) {
      // Convert camelCase or PascalCase to space-separated words
      const result = value
        // insert space before capital letters
        .replace(/([A-Z])/g, ' $1')
        // lowercase the whole string
        .toLowerCase()
        .trim() // remove leading/trailing spaces

      return result
    }

    return value
  }

  return (
    <div className={classes.container}>
      <div className={classes.labelRow}>
        <Typography className={classes.label}>{formatLabel(label)}</Typography>
      </div>

      <Typography className={small ? classes.valueSmall : classes.value}>
        {!showNormalized && (
          <span className={classes.formatTooltip}>
            {formatValue(label, value)}
            <span className={classes.tooltipOffset}>
              <MetricTooltip
                metric={METRICS[label]}
                classes={classes}
                note={small ? 'Using filtered time series' : undefined}
              />
            </span>
          </span>
        )}

        {/* Special NMAE normalized rendering */}
        {showNormalized && (
          <>
            <span className={classes.formatTooltip}>
              {formatValue(label, value)}
              <span className={classes.tooltipOffset}>
                <MetricTooltip
                  metric={METRICS[label]}
                  classes={classes}
                  note={small ? 'Using filtered time series' : undefined}
                />
              </span>
            </span>

            {' | '}

            <span className={classes.formatTooltip}>
              {formatValue('normalized', normalizedValue)}
              <span className={classes.tooltipOffset}>
                <MetricTooltip
                  metric={METRICS.normalized}
                  classes={classes}
                  note={small ? 'Using filtered time series' : undefined}
                />
              </span>
            </span>
          </>
        )}
      </Typography>
    </div>
  )
}

export default Parameters
